import React, { createContext, useContext } from 'react';
import PropTypes from 'prop-types';
import { AlertCircle, Filter } from 'lucide-react';
import IconButton from '../../ui/IconButton';
import Tooltip from '../../ui/Tooltip';
import { cx } from '../../ui/classNames';
import { HeaderTitle } from '../grid/WatchlistGridHeader';
import { MAX_ATTRIBUTE_CATEGORIES } from './categoryTools';

/**
 * What the category column headers show, from useCategoryColumnTools: the
 * filtered columns, the column whose filter is open, the columns over the
 * category limit and openFilter(columnKey, anchorEl). Headers read it here
 * rather than from their column definitions, so opening a filter does not
 * rebuild the grid's columns.
 */
export const CategoryColumnsContext = createContext(null);

// Quiet until the column is filtered: a borderless 24px button (30px for
// touch) in secondary text, framed on hover (the kit IconButton's frame is
// too heavy for a header). Filtered, it takes an accent tint and border.
const FILTER_BUTTON_CLASS_NAMES = Object.freeze({
    idle: cx(
        '!h-6 !w-6 !border-transparent !bg-transparent !text-content-secondary',
        'hover:!border-hairline hover:!bg-black/[0.04] dark:hover:!bg-white/[0.08]',
        '[@media(pointer:coarse)]:!h-[30px] [@media(pointer:coarse)]:!w-[30px]',
    ),
    filtered: cx(
        '!h-6 !w-6 !border-accent/[0.45] !bg-accent/[0.12] !text-accent dark:!bg-accent/[0.24]',
        'hover:!border-accent hover:!bg-accent/[0.18] dark:hover:!bg-accent/[0.32]',
        '[@media(pointer:coarse)]:!h-[30px] [@media(pointer:coarse)]:!w-[30px]',
    ),
});

/**
 * Header content of a category (attribute) column: its label, a filter
 * button that opens the column's category filter, and a mark when the
 * column holds more categories than can be saved. Render it from the
 * column's `header` function (with `meta.label` for menus).
 */
export default function CategoryColumnHeader({ columnKey, label }) {
    const tools = useContext(CategoryColumnsContext);
    const filtered = Boolean(tools?.filteredKeys.has(columnKey));
    const open = tools?.openKey === columnKey;
    const overLimitCount = tools?.overLimitCounts[columnKey] || 0;
    const filterLabel = filtered ? `Filter ${label} (filtered)` : `Filter ${label}`;
    return (
        <div className="flex min-w-0 items-center gap-0.5">
            <HeaderTitle label={label} />
            {overLimitCount > 0 && (
                <Tooltip content={`${overLimitCount} categories. At most ${MAX_ATTRIBUTE_CATEGORIES} can be saved.`}>
                    <span
                        role="img"
                        aria-label={`${label} has ${overLimitCount} categories, more than ${MAX_ATTRIBUTE_CATEGORIES}`}
                        // MUI's default error red, as the error-coloured icon had.
                        className="inline-flex shrink-0 text-[#d32f2f] dark:text-[#f44336]"
                    >
                        <AlertCircle aria-hidden="true" size={16} />
                    </span>
                </Tooltip>
            )}
            {tools && (
                <Tooltip content={filtered ? `${label} is filtered` : `Filter ${label}`} delay={600}>
                    <IconButton
                        label={filterLabel}
                        size="xs"
                        aria-haspopup="dialog"
                        aria-expanded={open}
                        className={filtered ? FILTER_BUTTON_CLASS_NAMES.filtered : FILTER_BUTTON_CLASS_NAMES.idle}
                        onClick={(event) => {
                            // The header sorts on click; this button only filters.
                            event.stopPropagation();
                            tools.openFilter(columnKey, event.currentTarget);
                        }}
                    >
                        <Filter aria-hidden="true" size={15} fill={filtered ? 'currentColor' : 'none'} />
                    </IconButton>
                </Tooltip>
            )}
        </div>
    );
}

CategoryColumnHeader.propTypes = {
    columnKey: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
};
